import React from 'react';
import { AIInferenceStreamUsage, AIToolInvocation, RiskScoreEvidence } from 'Generated/schema';
import { extractErrorMessage } from 'Helpers/utils';
import { useSnackbar } from 'pounceV2';
import { useGetAIInferenceStream } from '../graphql/getAiInferenceStream.generated';

const POLL_INTERVAL_MS = 500;

interface UseAIStreamManagementProps {
  streamId?: string;
  onStreamComplete?: (
    summary: string,
    userPrompt: string,
    usage?: AIInferenceStreamUsage,
    riskScoreEvidence?: RiskScoreEvidence | null,
  ) => void;
  onStreamError?: (error: string) => void;
}

interface UseAIStreamManagementReturn {
  aiSummary: string;
  aiSummaryError: string;
  initialUserPrompt: string;
  usage: AIInferenceStreamUsage | null;
  isLoading: boolean;
  isCancelled: boolean;
  cancelStream: () => void;
  resetStream: () => void;
  toolInvocations: AIToolInvocation[];
  riskScoreEvidence: RiskScoreEvidence | null;
}

/**
 * Hook for managing AI stream state, polling, and lifecycle
 * Extracts stream management logic from useFetchGenericAiSummary
 */
export const useAIStreamManagementV3 = ({
  streamId,
  onStreamComplete,
  onStreamError,
}: UseAIStreamManagementProps): UseAIStreamManagementReturn => {
  const { pushSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = React.useState(false);
  const [isCancelled, setIsCancelled] = React.useState(false);
  const [aiSummary, setAiSummary] = React.useState('');
  const [aiSummaryError, setAiSummaryError] = React.useState('');
  const [initialUserPrompt, setInitialUserPrompt] = React.useState('');
  const [usage, setUsage] = React.useState<AIInferenceStreamUsage | null>(null);

  // Reset state when streamId changes
  React.useEffect(() => {
    if (streamId) {
      setIsLoading(true);
      setIsCancelled(false);
    } else {
      // Full reset only when no stream
      setAiSummary('');
      setAiSummaryError('');
      setInitialUserPrompt('');
      setUsage(null);
      setIsCancelled(false);
      setIsLoading(false);
    }
  }, [streamId]);

  const {
    data: aiInferenceStreamResponse,
    error: aiInferenceStreamError,
    stopPolling,
  } = useGetAIInferenceStream({
    skip: !streamId,
    variables: { id: streamId ?? '' },
    pollInterval: POLL_INTERVAL_MS,
    notifyOnNetworkStatusChange: true,
    onError: err => {
      const errorMessage = extractErrorMessage(err) || 'Failed to fetch AI summary results';
      pushSnackbar({
        variant: 'error',
        title: errorMessage,
      });
      setIsLoading(false);
      onStreamError?.(errorMessage);
    },
    onCompleted: ({
      aiInferenceStream: {
        responseText,
        error,
        finished,
        userPrompt,
        usage: streamUsage,
        riskScoreEvidence: streamRiskEvidence,
      },
    }) => {
      // If the user pressed the stop button, don't affect the state further
      if (isCancelled) {
        return;
      }

      // If the user moved away then came back before the full summary was fetched
      if (!finished && !isLoading) {
        setIsLoading(true);
      }

      // Handle success and error responses separately
      setAiSummary(responseText || '');
      setAiSummaryError(error || '');
      setInitialUserPrompt(userPrompt || '');

      // Set usage data when available and stream is finished
      if (finished && streamUsage) {
        setUsage(streamUsage);
      }

      // Call completion callback when finished
      if (finished) {
        const finalResponse = responseText || error || '';
        onStreamComplete?.(
          finalResponse,
          userPrompt || '',
          streamUsage || undefined,
          streamRiskEvidence ?? null,
        );
      }
    },
  });

  const toolInvocations = aiInferenceStreamResponse?.aiInferenceStream?.toolInvocations || [];
  const riskScoreEvidence = aiInferenceStreamResponse?.aiInferenceStream?.riskScoreEvidence || null;

  // Stop polling when finished, errored, or streamId becomes undefined
  // Important: Only stop if the response is for the CURRENT stream to avoid race conditions
  React.useEffect(() => {
    const responseStreamId = aiInferenceStreamResponse?.aiInferenceStream?.streamId;
    const isCurrentStream = responseStreamId === streamId;

    if (!streamId) {
      // No stream ID means we should stop
      stopPolling();
      setIsLoading(false);
    } else if (isCurrentStream && aiInferenceStreamError) {
      // Error for the current stream
      stopPolling();
      setIsLoading(false);
    } else if (
      isCurrentStream &&
      (aiInferenceStreamResponse?.aiInferenceStream?.error ||
        aiInferenceStreamResponse?.aiInferenceStream?.finished)
    ) {
      // Current stream finished or errored
      stopPolling();
      setIsLoading(false);
    }
  }, [aiInferenceStreamResponse, streamId, aiInferenceStreamError, stopPolling]);

  // Stop polling on unmount
  React.useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const cancelStream = React.useCallback(() => {
    stopPolling();
    setIsLoading(false);
    setIsCancelled(true);
  }, [stopPolling]);

  const resetStream = React.useCallback(() => {
    setAiSummary('');
    setAiSummaryError('');
    setInitialUserPrompt('');
    setUsage(null);
    setIsCancelled(false);
    setIsLoading(false);
  }, []);

  // If the API already shows finished=true for the current stream, treat it as
  // not loading immediately — without this, there is a one-render window after
  // streamId changes where isLoading=true but the cached response is already
  // complete, which causes the typewriter effect to play on completed responses.
  const currentStreamData = aiInferenceStreamResponse?.aiInferenceStream;
  const isCurrentStream = currentStreamData?.streamId === streamId;
  const streamAlreadyFinished =
    isCurrentStream && Boolean(currentStreamData?.finished || currentStreamData?.error);
  const effectiveIsLoading = streamAlreadyFinished ? false : isLoading;

  return {
    aiSummary,
    aiSummaryError,
    initialUserPrompt,
    usage,
    isLoading: effectiveIsLoading,
    isCancelled,
    cancelStream,
    resetStream,
    toolInvocations,
    riskScoreEvidence,
  };
};
